import type {
  InvitationActivateResponse,
  InvitationValidationResponse,
} from '@bingads-webui-campaign-react/accept-invitation-api';

// Once an invitation has been activated, the code is consumed server-side — a second
// Validate/ActivationOptions call would fail. The activate response only lives in React state, so a
// browser refresh on the confirmation page would otherwise re-run Validate and surface an error page.
// To keep the confirmation stable across refresh, we persist the activate result, the identity type
// (the "Sign-in type" label), AND the Validate-derived fields the confirmation shows
// (customer/accounts/role) in sessionStorage, keyed by the invitation code, and restore them on mount.
// The confirmation no longer fetches AccessSummary, so this persisted copy is what lets a refresh
// render fully.
const ACTIVATION_STORAGE_PREFIX = 'accept-invitation:activated:';

// The Validate-derived fields the confirmation page needs, persisted for refresh survival (Validate
// can't be re-called on the consumed code). Intentionally excludes the PII sender fields
// (senderName/firstName/lastName) — the confirmation doesn't use them.
export type PersistedValidation = Pick<InvitationValidationResponse,
  'customerId' | 'customerName' | 'customerNumber' | 'email' | 'roleName' | 'accounts' | 'accountCount' | 'isCustomerLevelAccess'>;

export type PersistedActivation = {
  activationResponse: InvitationActivateResponse;
  identityType?: string;
  validation?: PersistedValidation;
};

export const toPersistedValidation = (data?: InvitationValidationResponse): PersistedValidation | undefined => (data
  ? {
    customerId: data.customerId,
    customerName: data.customerName,
    customerNumber: data.customerNumber,
    email: data.email,
    roleName: data.roleName,
    accounts: data.accounts,
    accountCount: data.accountCount,
    isCustomerLevelAccess: data.isCustomerLevelAccess,
  }
  : undefined);

const getActivationStorageKey = (code: string): string => `${ACTIVATION_STORAGE_PREFIX}${code}`;

export const readPersistedActivation = (code: string): PersistedActivation | null => {
  if (!code) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(getActivationStorageKey(code));
    return raw ? (JSON.parse(raw) as PersistedActivation) : null;
  } catch {
    return null;
  }
};

export const writePersistedActivation = (code: string, value: PersistedActivation): void => {
  if (!code) {
    return;
  }
  try {
    window.sessionStorage.setItem(getActivationStorageKey(code), JSON.stringify(value));
  } catch {
    // sessionStorage may be unavailable (private mode / quota); a refresh just falls back to the
    // normal API path. No functional regression versus today.
  }
};

// Drafts contain personal data. Restore only for the same authenticated identity and invitation,
// never for the invited email or a provider supplied in the URL. Unbound old drafts fail closed.
const FORM_DRAFT_STORAGE_PREFIX = 'accept-invitation:form-draft:';
const FORM_DRAFT_VERSION = 2;
const FORM_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type DraftIdentity = { username: string; provider: string };
export type DraftDiagnostic = {
  event: 'DraftRestored' | 'DraftDiscarded';
  reason: 'SameIdentity' | 'UnknownIdentity' | 'IdentityMismatch' | 'Legacy' | 'Stale' | 'Malformed' | 'StorageUnavailable';
  fieldCount: number;
};

export const getDraftIdentity = (username?: string, provider?: string): DraftIdentity | undefined => (
  typeof username === 'string' && username.trim() && typeof provider === 'string' && ['AAD', 'MSA', 'Google', 'Facebook'].includes(provider)
    ? { username: username.trim(), provider }
    : undefined
);

// Only these keys are persisted and restored. Anything else found in storage is ignored, so a
// stale or hand-edited payload can never inject unexpected fields into form state.
const DRAFT_TEXT_FIELDS = [
  'firstName', 'lastName', 'email', 'phoneNumber', 'country',
  'addressLine1', 'addressLine2', 'city', 'stateOrProvince', 'postalCode',
] as const;

const DRAFT_BOOLEAN_FIELDS = [
  'agreeToTerms', 'allowContactPreference', 'contactPreferenceEmail', 'contactPreferencePhone',
] as const;

export type PersistedFormDraft = Partial<
  Record<(typeof DRAFT_TEXT_FIELDS)[number], string> &
  Record<(typeof DRAFT_BOOLEAN_FIELDS)[number], boolean>
>;

const getFormDraftStorageKey = (code: string): string => `${FORM_DRAFT_STORAGE_PREFIX}${code}`;

// Copies only known keys, and only when the stored value has the expected primitive type, so a
// malformed payload degrades to "no draft" instead of corrupting form state.
const sanitizeDraft = (raw: unknown): PersistedFormDraft => {
  const draft: Record<string, string | boolean> = {};
  if (!raw || typeof raw !== 'object') {
    return draft;
  }
  const source = raw as Record<string, unknown>;
  DRAFT_TEXT_FIELDS.forEach((key) => {
    if (typeof source[key] === 'string') {
      draft[key] = source[key] as string;
    }
  });
  DRAFT_BOOLEAN_FIELDS.forEach((key) => {
    if (typeof source[key] === 'boolean') {
      draft[key] = source[key] as boolean;
    }
  });
  return draft;
};

export const clearPersistedFormDraft = (code: string): void => {
  if (!code) {
    return;
  }
  try {
    window.sessionStorage.removeItem(getFormDraftStorageKey(code));
  } catch {
    // Best effort: identity checks still prevent restoring a different user's data.
  }
};

export const readPersistedFormDraft = (
  code: string,
  identity?: DraftIdentity,
  // eslint-disable-next-line no-unused-vars
  report?: (diagnostic: DraftDiagnostic) => void
): PersistedFormDraft => {
  if (!code) {
    return {};
  }
  const discard = (reason: DraftDiagnostic['reason']): PersistedFormDraft => {
    clearPersistedFormDraft(code);
    report?.({ event: 'DraftDiscarded', reason, fieldCount: 0 });
    return {};
  };
  try {
    const raw = window.sessionStorage.getItem(getFormDraftStorageKey(code));
    if (!raw) return {};
    if (!getDraftIdentity(identity?.username, identity?.provider)) return discard('UnknownIdentity');
    let record;
    try {
      record = JSON.parse(raw);
    } catch {
      return discard('Malformed');
    }
    if (!record || typeof record !== 'object' || Array.isArray(record)) return discard('Malformed');
    if (record.version !== FORM_DRAFT_VERSION || !record.identity) return discard('Legacy');
    if (record.identity.username !== identity?.username || record.identity.provider !== identity?.provider) {
      return discard('IdentityMismatch');
    }
    if (!Number.isFinite(record.savedAt) || record.savedAt > Date.now() || Date.now() - record.savedAt > FORM_DRAFT_MAX_AGE_MS) {
      return discard('Stale');
    }
    if (!record.fields || typeof record.fields !== 'object' || Array.isArray(record.fields)) return discard('Malformed');
    const draft = sanitizeDraft(record.fields);
    report?.({ event: 'DraftRestored', reason: 'SameIdentity', fieldCount: Object.keys(draft).length });
    return draft;
  } catch {
    return discard('StorageUnavailable');
  }
};

export const writePersistedFormDraft = (code: string, value: PersistedFormDraft, identity?: DraftIdentity): void => {
  if (!code) {
    return;
  }
  if (!getDraftIdentity(identity?.username, identity?.provider)) {
    clearPersistedFormDraft(code);
    return;
  }
  try {
    window.sessionStorage.setItem(getFormDraftStorageKey(code), JSON.stringify({
      version: FORM_DRAFT_VERSION, identity, savedAt: Date.now(), fields: sanitizeDraft(value),
    }));
  } catch {
    // Private mode / quota: the draft is a convenience, so failing to store it must never surface.
  }
};

// One-shot marker for the `ReauthRequired` recovery bounce (see accept-invitation-page). The bounce
// is a full-page navigation through the IdP, so an in-memory ref cannot survive it — and because it
// is a JS navigation rather than a server redirect chain, the browser's redirect limit never trips.
// Without a marker that outlives the navigation, a challenge that fails to repair the token cache
// would bounce forever. sessionStorage is the right lifetime: it survives the round-trip and dies
// with the tab.
const REAUTH_STORAGE_PREFIX = 'accept-invitation:reauth-attempted:';

const getReauthStorageKey = (code: string): string => `${REAUTH_STORAGE_PREFIX}${code}`;

export const hasAttemptedReauth = (code: string): boolean => {
  if (!code) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(getReauthStorageKey(code)) !== null;
  } catch {
    return false;
  }
};

export const markReauthAttempted = (code: string): void => {
  if (!code) {
    return;
  }
  try {
    window.sessionStorage.setItem(getReauthStorageKey(code), '1');
  } catch {
    // Storage unavailable — the guard degrades to today's unguarded behaviour rather than blocking
    // a recovery that might work.
  }
};

// Cleared once ActivationOptions succeeds, so the guard caps one bounce per failure episode rather
// than one per tab for the lifetime of the tab.
export const clearReauthAttempted = (code: string): void => {
  if (!code) {
    return;
  }
  try {
    window.sessionStorage.removeItem(getReauthStorageKey(code));
  } catch {
    // Nothing to do — a stale marker only costs one forgone retry.
  }
};
